import React, { useEffect, useState } from 'react';
import {
    Table,
    Card,
    Row,
    Col,
    Statistic,
    Select,
    DatePicker,
    Tag,
    Button,
    Space,
    Typography,
    Spin,
    Progress,
    Alert,
    Modal,
    Form,
    InputNumber,
    Input,
    Empty,
    message,
    Tabs,
    Popconfirm,
    Divider
} from 'antd';
import {
    CalendarOutlined,
    UserOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    EditOutlined,
    PlusOutlined,
    DeleteOutlined,
    SaveOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { attendanceAPI, departmentAPI } from '../../services/api';
import dayjs from 'dayjs';
import BulkAnnualLeaveModal from '../../components/BulkAnnualLeaveModal';
import VacationCalendarModal from '../../components/VacationCalendarModal';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const AttendanceManagementPage = () => {
    const [loading, setLoading] = useState(false);
    const [usersAttendance, setUsersAttendance] = useState([]);
    const [filteredUsersAttendance, setFilteredUsersAttendance] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [vacationCalendarVisible, setVacationCalendarVisible] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [statistics, setStatistics] = useState(null);
    
    // 연차 수정 모달
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm] = Form.useForm();
    
    // 승인 대기 근태 모달
    const [showPendingModal, setShowPendingModal] = useState(false);
    const [pendingAttendances, setPendingAttendances] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [viewMode, setViewMode] = useState('all'); // 항상 'all' 모드
    
    // 연차 일괄등록 모달
    const [showBulkLeaveModal, setShowBulkLeaveModal] = useState(false);
    
    // 연차 사용 내역 관련
    const [userAttendanceHistory, setUserAttendanceHistory] = useState([]);
    const [filteredAttendanceHistory, setFilteredAttendanceHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [editingAttendance, setEditingAttendance] = useState(null);
    const [attendanceEditForm] = Form.useForm();
    const [newAttendanceForm] = Form.useForm();
    const [attendanceFilter, setAttendanceFilter] = useState('all'); // all, annual_impact, no_annual_impact

    useEffect(() => {
        loadDepartments();
        loadUsersAttendance();
    }, [selectedYear, selectedDepartment]);

    // 데이터 로드 시 필터링된 데이터 초기화
    useEffect(() => {
        setFilteredUsersAttendance(usersAttendance);
    }, [usersAttendance]);

    // 검색 기능
    useEffect(() => {
        if (!searchText) {
            setFilteredUsersAttendance(usersAttendance);
        } else {
            const filtered = usersAttendance.filter(item =>
                item.user.name.toLowerCase().includes(searchText.toLowerCase()) ||
                item.user.employee_id.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredUsersAttendance(filtered);
        }
    }, [searchText, usersAttendance]);

    const loadDepartments = async () => {
        try {
            const response = await departmentAPI.getAll();
            const depts = Array.isArray(response.data) ? response.data : response.data.departments || [];
            setDepartments(depts);
        } catch (error) {
            console.error('Load departments error:', error);
        }
    };

    const loadUsersAttendance = async () => {
        setLoading(true);
        try {
            const params = { year: selectedYear };
            if (selectedDepartment) {
                params.department_id = selectedDepartment;
            }

            const response = await attendanceAPI.getAllUsersStatus(params);
            setUsersAttendance(response.data.users);
            
            // 통계 계산
            calculateStatistics(response.data.users);
        } catch (error) {
            console.error('Load users attendance error:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStatistics = (users) => {
        const totalUsers = users.length;
        const totalAnnualLeave = users.reduce((sum, user) => sum + user.balance.total_annual_leave, 0);
        const totalUsedLeave = users.reduce((sum, user) => sum + user.balance.used_annual_leave, 0);
        const totalRemainingLeave = users.reduce((sum, user) => sum + user.balance.remaining_annual_leave, 0);
        const lowLeaveUsers = users.filter(user => user.balance.remaining_annual_leave < 3).length;
        const pendingApprovals = users.reduce((sum, user) => sum + user.pending_count, 0);

        setStatistics({
            totalUsers,
            totalAnnualLeave,
            totalUsedLeave,
            totalRemainingLeave,
            averageUsedRate: totalUsers > 0 ? (totalUsedLeave / totalAnnualLeave * 100) : 0,
            lowLeaveUsers,
            pendingApprovals
        });
    };

    // 연차 수정 모달 열기
    const handleEditAnnualLeave = (record) => {
        setEditingUser(record);
        editForm.setFieldsValue({
            total_annual_leave: record.balance.total_annual_leave,
            bonus_days: record.balance.bonus_days || 0
        });
        setEditModalVisible(true);
        
        // 해당 사용자의 연차 사용 내역 로드
        loadUserAttendanceHistory(record.user.id, selectedYear);
    };
    
    // 사용자 연차 사용 내역 로드
    const loadUserAttendanceHistory = async (userId, year) => {
        setHistoryLoading(true);
        try {
            const response = await attendanceAPI.getUserAttendanceHistory(userId, { year });
            if (response.data.success) {
                setUserAttendanceHistory(response.data.attendances);
            }
        } catch (error) {
            console.error('Load user attendance history error:', error);
            message.error('연차 사용 내역 조회에 실패했습니다');
        } finally {
            setHistoryLoading(false);
        }
    };
    
    // 연차 수정 시작
    const handleEditAttendance = (attendance) => {
        setEditingAttendance(attendance);
        attendanceEditForm.setFieldsValue({
            attendance_type: attendance.attendance_type,
            start_date: dayjs(attendance.start_date),
            end_date: dayjs(attendance.end_date),
            reason: attendance.reason
        });
    };
    
    // 연차 수정 저장
    const handleSaveAttendance = async () => {
        try {
            const values = await attendanceEditForm.validateFields();
            
            await attendanceAPI.updateUserAttendance(editingAttendance.id, {
                attendance_type: values.attendance_type,
                start_date: values.start_date.format('YYYY-MM-DD'),
                end_date: values.end_date.format('YYYY-MM-DD'),
                reason: values.reason
            });

            message.success('연차가 성공적으로 수정되었습니다');
            setEditingAttendance(null);
            attendanceEditForm.resetFields();
            
            // 목록 새로고침
            loadUserAttendanceHistory(editingUser.user.id, selectedYear);
            loadUsersAttendance();
        } catch (error) {
            message.error(error.response?.data?.message || '연차 수정에 실패했습니다');
        }
    };
    
    // 연차 삭제
    const handleDeleteAttendance = async (attendanceId) => {
        try {
            await attendanceAPI.deleteUserAttendance(attendanceId);
            message.success('연차가 성공적으로 삭제되었습니다');
            
            // 목록 새로고침
            loadUserAttendanceHistory(editingUser.user.id, selectedYear);
            loadUsersAttendance();
        } catch (error) {
            message.error(error.response?.data?.message || '연차 삭제에 실패했습니다');
        }
    };
    
    // 새 연차 추가
    const handleAddAttendance = async () => {
        try {
            const values = await newAttendanceForm.validateFields();
            
            await attendanceAPI.createUserAttendance({
                user_id: editingUser.user.id,
                attendance_type: values.attendance_type,
                start_date: values.start_date.format('YYYY-MM-DD'),
                end_date: values.end_date.format('YYYY-MM-DD'),
                reason: values.reason || '관리자 등록'
            });

            message.success('연차가 성공적으로 등록되었습니다');
            newAttendanceForm.resetFields();
            
            // 목록 새로고침
            loadUserAttendanceHistory(editingUser.user.id, selectedYear);
            loadUsersAttendance();
        } catch (error) {
            message.error(error.response?.data?.message || '연차 등록에 실패했습니다');
        }
    };

    // 연차 수정 처리
    const handleUpdateAnnualLeave = async () => {
        try {
            const values = await editForm.validateFields();
            
            await attendanceAPI.updateAnnualLeaveBalance(
                editingUser.user.id,
                selectedYear,
                values
            );

            message.success('연차가 성공적으로 수정되었습니다');
            setEditModalVisible(false);
            loadUsersAttendance();
        } catch (error) {
            message.error(error.response?.data?.message || '연차 수정에 실패했습니다');
        }
    };
    
    // 연차 모달 닫기
    const handleCloseEditModal = () => {
        setEditModalVisible(false);
        setEditingAttendance(null);
        setUserAttendanceHistory([]);
        setFilteredAttendanceHistory([]);
        setAttendanceFilter('all');
        editForm.resetFields();
        attendanceEditForm.resetFields();
        newAttendanceForm.resetFields();
    };

    // 승인 대기 근태 조회 - 승인 권한 범위 내 모든 승인 대기 근태 조회
    const showPendingAttendances = async () => {
        setSelectedUser(null);
        setViewMode('all');
        setLoading(true);
        try {
            const response = await attendanceAPI.getPending(); // user_id 없이 전체 조회
            setPendingAttendances(response.data.attendances);
            setShowPendingModal(true);
        } catch (error) {
            message.error('승인 대기 근태 조회에 실패했습니다');
            console.error('Fetch pending attendances error:', error);
        } finally {
            setLoading(false);
        }
    };

    // 근태 승인
    const handleApprove = async (attendanceId) => {
        setApprovalLoading(true);
        try {
            await attendanceAPI.approve(attendanceId);
            message.success('근태 신청이 승인되었습니다');
            
            // 목록 새로고침
            loadUsersAttendance();
            
            // 현재 모달의 목록 새로고침
            if (viewMode === 'individual' && selectedUser) {
                const response = await attendanceAPI.getPending({ user_id: selectedUser.id });
                setPendingAttendances(response.data.attendances);
            } else if (viewMode === 'all') {
                const response = await attendanceAPI.getPending();
                setPendingAttendances(response.data.attendances);
            }
        } catch (error) {
            message.error(error.response?.data?.message || '승인 처리 중 오류가 발생했습니다');
        } finally {
            setApprovalLoading(false);
        }
    };

    // 근태 반려
    const handleReject = async (attendanceId) => {
        setApprovalLoading(true);
        try {
            await attendanceAPI.reject(attendanceId, { 
                rejection_reason: '관리자에 의해 반려됨' 
            });
            message.success('근태가 반려되었습니다');
            
            // 목록 새로고침
            setPendingAttendances(prev => prev.filter(att => att.id !== attendanceId));
            await loadUsersAttendance();
            
            // 현재 모달의 목록 새로고침
            if (viewMode === 'individual' && selectedUser) {
                const response = await attendanceAPI.getPending({ user_id: selectedUser.id });
                setPendingAttendances(response.data.attendances);
            } else if (viewMode === 'all') {
                const response = await attendanceAPI.getPending();
                setPendingAttendances(response.data.attendances);
            }
        } catch (error) {
            message.error(error.response?.data?.message || '반려 처리에 실패했습니다');
        } finally {
            setApprovalLoading(false);
        }
    };

    // 근태 유형 이름 매핑 (대시보드와 통일)
    const getAttendanceTypeName = (type) => {
        const typeMap = {
            'annual_leave': '연차',
            'sick_leave': '병가',
            'personal_leave': '건강검진', // 대시보드와 통일: '개인사정' → '건강검진'
            'bereavement_leave': '경조휴가', // 대시보드와 통일: '경조사' → '경조휴가'
            'half_day_morning': '오전반차', // 대시보드와 통일: 띄어쓰기 제거
            'half_day_afternoon': '오후반차', // 대시보드와 통일: 띄어쓰기 제거
            'health_checkup': '건강검진',
            'maternity_leave': '출산휴가',
            'paternity_leave': '육아휴직',
            'special_leave': '특별휴가'
        };
        return typeMap[type] || type;
    };

    // 연차에 영향을 미치는 휴가 유형 확인
    const isAnnualLeaveImpacting = (type) => {
        const annualImpactingTypes = ['annual_leave', 'half_day_morning', 'half_day_afternoon'];
        return annualImpactingTypes.includes(type);
    };

    // 연차 사용내역 필터링
    useEffect(() => {
        if (attendanceFilter === 'all') {
            setFilteredAttendanceHistory(userAttendanceHistory);
        } else if (attendanceFilter === 'annual_impact') {
            setFilteredAttendanceHistory(
                userAttendanceHistory.filter(item => isAnnualLeaveImpacting(item.attendance_type))
            );
        } else if (attendanceFilter === 'no_annual_impact') {
            setFilteredAttendanceHistory(
                userAttendanceHistory.filter(item => !isAnnualLeaveImpacting(item.attendance_type))
            );
        }
    }, [userAttendanceHistory, attendanceFilter]);

    const columns = [
        {
            title: '사번',
            dataIndex: ['user', 'employee_id'],
            key: 'employee_id',
            width: 100,
            sorter: (a, b) => a.user.employee_id.localeCompare(b.user.employee_id),
        },
        {
            title: '이름',
            dataIndex: ['user', 'name'],
            key: 'name',
            width: 100,
            sorter: (a, b) => a.user.name.localeCompare(b.user.name),
        },
        {
            title: '부서',
            dataIndex: ['user', 'department', 'name'],
            key: 'department',
            width: 120,
            sorter: (a, b) => (a.user.department?.name || '').localeCompare(b.user.department?.name || ''),
            render: (deptName, record) => (
                <Tag color="blue">{record.user.department?.code}</Tag>
            )
        },
        {
            title: '입사일',
            dataIndex: ['user', 'hire_date'],
            key: 'hire_date',
            width: 120,
            sorter: (a, b) => {
                const dateA = a.user.hire_date ? dayjs(a.user.hire_date) : dayjs('1900-01-01');
                const dateB = b.user.hire_date ? dayjs(b.user.hire_date) : dayjs('1900-01-01');
                return dateA.isAfter(dateB) ? 1 : -1;
            },
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
        },
        {
            title: '총 연차',
            dataIndex: ['balance', 'total_annual_leave'],
            key: 'total_annual_leave',
            width: 80,
            align: 'center',
            sorter: (a, b) => Number(a.balance.total_annual_leave) - Number(b.balance.total_annual_leave),
            render: (days) => `${Number(days).toFixed(1)}일`
        },
        {
            title: '사용 연차',
            dataIndex: ['balance', 'used_annual_leave'],
            key: 'used_annual_leave',
            width: 80,
            align: 'center',
            sorter: (a, b) => Number(a.balance.used_annual_leave) - Number(b.balance.used_annual_leave),
            render: (days) => `${Number(days).toFixed(1)}일`
        },
        {
            title: '잔여 연차',
            dataIndex: ['balance', 'remaining_annual_leave'],
            key: 'remaining_annual_leave',
            width: 80,
            align: 'center',
            sorter: (a, b) => Number(a.balance.remaining_annual_leave) - Number(b.balance.remaining_annual_leave),
            render: (days) => (
                <span style={{ color: days < 3 ? '#cf1322' : '#52c41a', fontWeight: 'bold' }}>
                    {Number(days).toFixed(1)}일
                </span>
            )
        },
        {
            title: '사용률',
            key: 'usage_rate',
            width: 120,
            align: 'center',
            sorter: (a, b) => {
                const rateA = (a.balance.used_annual_leave / a.balance.total_annual_leave) * 100;
                const rateB = (b.balance.used_annual_leave / b.balance.total_annual_leave) * 100;
                return rateA - rateB;
            },
            render: (_, record) => {
                const rate = (record.balance.used_annual_leave / record.balance.total_annual_leave) * 100;
                return (
                    <Progress
                        percent={rate}
                        size="small"
                        format={(percent) => `${percent.toFixed(1)}%`}
                        strokeColor={rate > 80 ? '#52c41a' : rate > 50 ? '#faad14' : '#ff7875'}
                    />
                );
            }
        },
        {
            title: '승인 대기',
            dataIndex: 'pending_count',
            key: 'pending_count',
            width: 80,
            align: 'center',
            sorter: (a, b) => Number(a.pending_count) - Number(b.pending_count),
            render: (count, record) => (
                count > 0 ? (
                    <Tag color="orange">
                        {count}건
                    </Tag>
                ) : (
                    <Tag color="green">0건</Tag>
                )
            )
        },
        {
            title: '작업',
            key: 'action',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditAnnualLeave(record)}
                    title="연차 수정"
                >
                    수정
                </Button>
            )
        }
    ];

    const yearOptions = [];
    const currentYear = new Date().getFullYear();
    for (let year = currentYear; year >= currentYear - 5; year--) {
        yearOptions.push(
            <Option key={year} value={year}>
                {year}년
            </Option>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={2} style={{ margin: 0 }}>근태 현황 관리</Title>
                <Button 
                    type="default" 
                    icon={<CalendarOutlined />} 
                    onClick={() => setVacationCalendarVisible(true)}
                >
                    휴가현황
                </Button>
            </div>

            <Card style={{ marginBottom: 24 }}>
                <Row gutter={16}>
                    <Col>
                        <Select
                            value={selectedYear}
                            onChange={setSelectedYear}
                            style={{ width: 120 }}
                        >
                            {yearOptions}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            value={selectedDepartment}
                            onChange={setSelectedDepartment}
                            placeholder="부서 선택"
                            style={{ width: 200 }}
                        >
                            <Option value={null}>전체 부서</Option>
                            {departments.map(dept => (
                                <Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* 관리 기능 버튼들 */}
            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <Text strong>근태 관리 기능:</Text>
                        <Space>
                            <Button 
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setShowBulkLeaveModal(true)}
                            >
                                연차 일괄등록
                            </Button>
                            {statistics && statistics.pendingApprovals > 0 && (
                                <Button 
                                    type="default"
                                    icon={<ExclamationCircleOutlined />}
                                    onClick={showPendingAttendances}
                                >
                                    승인 대기 목록 ({statistics.pendingApprovals}건)
                                </Button>
                            )}
                        </Space>
                    </div>
                </div>
            </Card>

            {statistics && (
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} lg={4}>
                        <Card>
                            <Statistic
                                title="총 직원수"
                                value={statistics.totalUsers}
                                prefix={<UserOutlined />}
                                suffix="명"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                        <Card>
                            <Statistic
                                title="전체 연차"
                                value={statistics.totalAnnualLeave}
                                precision={1}
                                prefix={<CalendarOutlined />}
                                suffix="일"
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                        <Card>
                            <Statistic
                                title="사용 연차"
                                value={statistics.totalUsedLeave}
                                precision={1}
                                prefix={<CheckCircleOutlined />}
                                suffix="일"
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                        <Card>
                            <Statistic
                                title="잔여 연차"
                                value={statistics.totalRemainingLeave}
                                precision={1}
                                prefix={<CloseCircleOutlined />}
                                suffix="일"
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                        <Card>
                            <Statistic
                                title="연차 소진률"
                                value={statistics.averageUsedRate}
                                precision={1}
                                prefix={<TeamOutlined />}
                                suffix="%"
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                        <Card>
                            <Statistic
                                title="승인 대기"
                                value={statistics.pendingApprovals}
                                prefix={<ExclamationCircleOutlined />}
                                suffix="건"
                                valueStyle={{ color: statistics.pendingApprovals > 0 ? '#cf1322' : '#52c41a' }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {statistics && statistics.lowLeaveUsers > 0 && (
                <Alert
                    message={`잔여 연차가 3일 미만인 직원이 ${statistics.lowLeaveUsers}명 있습니다.`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Card 
                title={`${selectedYear}년 직원 근태 현황`}
                extra={
                    <Input
                        placeholder="직원명 또는 사번으로 검색"
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 250 }}
                        allowClear
                    />
                }
            >
                <Table
                    loading={loading}
                    dataSource={filteredUsersAttendance}
                    columns={columns}
                    rowKey={(record) => record.user.id}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `총 ${total}명`
                    }}
                    scroll={{ x: 1000 }}
                    size="middle"
                />
            </Card>

            {/* 연차 관리 모달 */}
            <Modal
                title={`${editingUser?.user?.name}님의 ${selectedYear}년 연차 관리`}
                open={editModalVisible}
                onCancel={handleCloseEditModal}
                footer={null}
                width={900}
            >
                <Tabs defaultActiveKey="1">
                    <TabPane tab="연차 설정" key="1">
                        <Form
                            form={editForm}
                            layout="vertical"
                        >
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="total_annual_leave"
                                        label="총 연차"
                                        rules={[
                                            { required: true, message: '총 연차를 입력하세요' },
                                            { type: 'number', min: 0, max: 50, message: '0-50일 사이로 입력하세요' }
                                        ]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            placeholder="총 연차 (일)"
                                            min={0}
                                            max={50}
                                            step={0.5}
                                            addonAfter="일"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="bonus_days"
                                        label="보너스 연차"
                                        rules={[
                                            { type: 'number', min: 0, max: 20, message: '0-20일 사이로 입력하세요' }
                                        ]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            placeholder="보너스 연차 (일)"
                                            min={0}
                                            max={20}
                                            step={0.5}
                                            addonAfter="일"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="reason"
                                label="수정 사유"
                            >
                                <Input.TextArea 
                                    rows={3}
                                    placeholder="연차 수정 사유를 입력하세요 (선택사항)"
                                />
                            </Form.Item>

                            <Alert
                                message="안내사항"
                                description="총 연차는 입사일 기준으로 자동 계산되지만, 특별한 사유로 조정할 수 있습니다. 보너스 연차는 추가로 지급하는 연차입니다."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            
                            <div style={{ textAlign: 'right' }}>
                                <Button 
                                    type="primary" 
                                    onClick={handleUpdateAnnualLeave}
                                >
                                    연차 설정 저장
                                </Button>
                            </div>
                        </Form>
                    </TabPane>

                    <TabPane tab="연차 사용 내역" key="2">
                        <div style={{ marginBottom: 16 }}>
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Space>
                                        <Text strong>휴가 사용 내역 관리</Text>
                                        <Select
                                            value={attendanceFilter}
                                            onChange={setAttendanceFilter}
                                            style={{ width: 150 }}
                                            size="small"
                                        >
                                            <Option value="all">전체 보기</Option>
                                            <Option value="annual_impact">연차 영향</Option>
                                            <Option value="no_annual_impact">연차 비영향</Option>
                                        </Select>
                                    </Space>
                                </Col>
                                <Col>
                                    <Button 
                                        type="primary" 
                                        icon={<PlusOutlined />}
                                        onClick={() => newAttendanceForm.resetFields()}
                                    >
                                        새 휴가 등록
                                    </Button>
                                </Col>
                            </Row>
                        </div>

                        {/* 새 연차 등록 폼 */}
                        <Card size="small" style={{ marginBottom: 16 }}>
                            <Title level={5}>새 연차 등록</Title>
                            <Form
                                form={newAttendanceForm}
                                layout="vertical"
                            >
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item
                                            name="attendance_type"
                                            label="연차 구분"
                                            rules={[{ required: true, message: '연차 구분을 선택하세요' }]}
                                        >
                                            <Select placeholder="연차 구분 선택">
                                                <Option value="annual_leave">종일 연차</Option>
                                                <Option value="half_day_morning">오전 반차</Option>
                                                <Option value="half_day_afternoon">오후 반차</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="start_date"
                                            label="시작일"
                                            rules={[{ required: true, message: '시작일을 선택하세요' }]}
                                        >
                                            <DatePicker 
                                                style={{ width: '100%' }} 
                                                format="YYYY-MM-DD"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="end_date"
                                            label="종료일"
                                            rules={[{ required: true, message: '종료일을 선택하세요' }]}
                                        >
                                            <DatePicker 
                                                style={{ width: '100%' }} 
                                                format="YYYY-MM-DD"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={16}>
                                        <Form.Item
                                            name="reason"
                                            label="사유"
                                        >
                                            <Input placeholder="연차 사유 (선택사항)" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8} style={{ display: 'flex', alignItems: 'end' }}>
                                        <Form.Item>
                                            <Button 
                                                type="primary" 
                                                icon={<SaveOutlined />}
                                                onClick={handleAddAttendance}
                                            >
                                                등록
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>

                        {/* 휴가 사용 내역 목록 */}
                        <Table
                            loading={historyLoading}
                            dataSource={filteredAttendanceHistory}
                            rowKey="id"
                            size="small"
                            scroll={{ y: 300 }}
                            pagination={false}
                            columns={[
                                {
                                    title: '휴가 구분',
                                    dataIndex: 'attendance_type',
                                    width: 120,
                                    render: (type) => {
                                        const isImpacting = isAnnualLeaveImpacting(type);
                                        const color = isImpacting ? 'blue' : 'green';
                                        return (
                                            <Space>
                                                <Tag color={color}>{getAttendanceTypeName(type)}</Tag>
                                                {!isImpacting && <Tag color="orange" size="small">비영향</Tag>}
                                            </Space>
                                        );
                                    }
                                },
                                {
                                    title: '시작일',
                                    dataIndex: 'start_date',
                                    width: 100,
                                    render: (date) => dayjs(date).format('YYYY-MM-DD')
                                },
                                {
                                    title: '종료일',
                                    dataIndex: 'end_date',
                                    width: 100,
                                    render: (date) => dayjs(date).format('YYYY-MM-DD')
                                },
                                {
                                    title: '사용일수',
                                    dataIndex: 'days_count',
                                    width: 80,
                                    align: 'center',
                                    render: (days) => `${Number(days).toFixed(1)}일`
                                },
                                {
                                    title: '상태',
                                    dataIndex: 'status',
                                    width: 80,
                                    render: (status) => {
                                        const statusMap = {
                                            'approved': { color: 'green', text: '승인' },
                                            'pending': { color: 'orange', text: '대기' },
                                            'rejected': { color: 'red', text: '반려' }
                                        };
                                        const statusInfo = statusMap[status] || { color: 'default', text: status };
                                        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
                                    }
                                },
                                {
                                    title: '사유',
                                    dataIndex: 'reason',
                                    ellipsis: true,
                                    render: (text) => text || '-'
                                },
                                {
                                    title: '작업',
                                    key: 'action',
                                    width: 120,
                                    render: (_, record) => {
                                        const canEdit = isAnnualLeaveImpacting(record.attendance_type);
                                        
                                        if (!canEdit) {
                                            return (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    개인 신청만 가능
                                                </Text>
                                            );
                                        }

                                        return (
                                            <Space size="small">
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => handleEditAttendance(record)}
                                                >
                                                    수정
                                                </Button>
                                                <Popconfirm
                                                    title="정말 삭제하시겠습니까?"
                                                    onConfirm={() => handleDeleteAttendance(record.id)}
                                                >
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                    >
                                                        삭제
                                                    </Button>
                                                </Popconfirm>
                                            </Space>
                                        );
                                    }
                                }
                            ]}
                        />

                        {/* 연차 수정 폼 */}
                        {editingAttendance && (
                            <>
                                <Divider />
                                <Card size="small">
                                    <Title level={5}>연차 수정</Title>
                                    <Form
                                        form={attendanceEditForm}
                                        layout="vertical"
                                    >
                                        <Row gutter={16}>
                                            <Col span={8}>
                                                <Form.Item
                                                    name="attendance_type"
                                                    label="연차 구분"
                                                    rules={[{ required: true, message: '연차 구분을 선택하세요' }]}
                                                >
                                                    <Select placeholder="연차 구분 선택">
                                                        <Option value="annual_leave">종일 연차</Option>
                                                        <Option value="half_day_morning">오전 반차</Option>
                                                        <Option value="half_day_afternoon">오후 반차</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item
                                                    name="start_date"
                                                    label="시작일"
                                                    rules={[{ required: true, message: '시작일을 선택하세요' }]}
                                                >
                                                    <DatePicker 
                                                        style={{ width: '100%' }} 
                                                        format="YYYY-MM-DD"
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item
                                                    name="end_date"
                                                    label="종료일"
                                                    rules={[{ required: true, message: '종료일을 선택하세요' }]}
                                                >
                                                    <DatePicker 
                                                        style={{ width: '100%' }} 
                                                        format="YYYY-MM-DD"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={16}>
                                                <Form.Item
                                                    name="reason"
                                                    label="사유"
                                                >
                                                    <Input placeholder="연차 사유" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8} style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
                                                <Form.Item>
                                                    <Button 
                                                        type="primary" 
                                                        icon={<SaveOutlined />}
                                                        onClick={handleSaveAttendance}
                                                    >
                                                        저장
                                                    </Button>
                                                </Form.Item>
                                                <Form.Item>
                                                    <Button onClick={() => setEditingAttendance(null)}>
                                                        취소
                                                    </Button>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card>
                            </>
                        )}
                    </TabPane>
                </Tabs>
            </Modal>

            {/* 승인 대기 근태 목록 모달 */}
            <Modal
                title="승인 대기 근태 목록"
                open={showPendingModal}
                onCancel={() => setShowPendingModal(false)}
                footer={null}
                width={900}
            >
                {pendingAttendances.length === 0 ? (
                    <Empty description="승인 대기 중인 근태가 없습니다." />
                ) : (
                    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {pendingAttendances.map(attendance => (
                            <Card 
                                key={attendance.id}
                                size="small"
                                style={{ marginBottom: 16 }}
                                title={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span>{getAttendanceTypeName(attendance.attendance_type)}</span>
                                            <Tag color="blue">{attendance.user?.name}</Tag>
                                            {attendance.leave_info?.is_annual_leave && (
                                                <Tag color="purple">
                                                    연차 {attendance.leave_info.current_request_days}일 신청
                                                </Tag>
                                            )}
                                        </div>
                                        <Tag color="orange">승인 대기</Tag>
                                    </div>
                                }
                            >
                                <div style={{ marginBottom: 12 }}>
                                    <Text strong>상신자: </Text>
                                    <Text>{attendance.user?.name} ({attendance.user?.department?.name})</Text>
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <Text strong>휴가 구분: </Text>
                                    <Tag color="cyan">{getAttendanceTypeName(attendance.attendance_type)}</Tag>
                                    {attendance.leave_info?.current_request_days > 0 && (
                                        <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>
                                            ({attendance.leave_info.current_request_days}일)
                                        </span>
                                    )}
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <Text strong>신청 기간: </Text>
                                    <Text>{dayjs(attendance.start_date).format('YYYY-MM-DD')} ~ {dayjs(attendance.end_date).format('YYYY-MM-DD')}</Text>
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <Text strong>신청일: </Text>
                                    <Text>{dayjs(attendance.created_at).format('YYYY-MM-DD HH:mm')}</Text>
                                </div>
                                
                                {/* 연차 정보 표시 (연차 관련 신청인 경우만) */}
                                {attendance.leave_info?.is_annual_leave && (
                                    <div style={{ 
                                        marginBottom: 12, 
                                        padding: 12, 
                                        backgroundColor: '#f8f9fa', 
                                        borderRadius: 6,
                                        border: '1px solid #e9ecef'
                                    }}>
                                        <Text strong style={{ display: 'block', marginBottom: 8, color: '#495057' }}>
                                            📊 {attendance.leave_info.year}년 연차 현황
                                        </Text>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <div style={{ fontSize: 12 }}>
                                                    <div style={{ marginBottom: 4 }}>
                                                        <Text strong>총 연차: </Text>
                                                        <span style={{ color: '#1890ff' }}>
                                                            {attendance.leave_info.total_annual_leave}일
                                                        </span>
                                                    </div>
                                                    <div style={{ marginBottom: 4 }}>
                                                        <Text strong>사용 연차: </Text>
                                                        <span style={{ color: '#fa8c16' }}>
                                                            {attendance.leave_info.used_annual_leave}일
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <Text strong>사용률: </Text>
                                                        <span style={{ 
                                                            color: attendance.leave_info.current_usage_rate > 80 ? '#52c41a' : 
                                                                   attendance.leave_info.current_usage_rate > 50 ? '#faad14' : '#ff7875'
                                                        }}>
                                                            {attendance.leave_info.current_usage_rate}%
                                                        </span>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col span={12}>
                                                <div style={{ fontSize: 12 }}>
                                                    <div style={{ marginBottom: 4 }}>
                                                        <Text strong>승인 후 사용: </Text>
                                                        <span style={{ color: '#f5222d', fontWeight: 'bold' }}>
                                                            {attendance.leave_info.after_approval_used}일
                                                        </span>
                                                    </div>
                                                    <div style={{ marginBottom: 4 }}>
                                                        <Text strong>승인 후 잔여: </Text>
                                                        <span style={{ 
                                                            color: attendance.leave_info.after_approval_remaining < 3 ? '#f5222d' : '#52c41a',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {attendance.leave_info.after_approval_remaining}일
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <Text strong>승인 후 사용률: </Text>
                                                        <span style={{ 
                                                            color: attendance.leave_info.after_approval_rate > 80 ? '#52c41a' : 
                                                                   attendance.leave_info.after_approval_rate > 50 ? '#faad14' : '#ff7875',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {attendance.leave_info.after_approval_rate}%
                                                        </span>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                                
                                {attendance.reason && (
                                    <div style={{ marginBottom: 12 }}>
                                        <Text strong>사유: </Text>
                                        <Text>{attendance.reason}</Text>
                                    </div>
                                )}
                                <div style={{ textAlign: 'right', marginTop: 16 }}>
                                    <Button
                                        type="primary"
                                        size="small"
                                        loading={approvalLoading}
                                        onClick={() => handleApprove(attendance.id)}
                                        style={{ marginRight: 8 }}
                                    >
                                        승인
                                    </Button>
                                    <Button
                                        danger
                                        size="small"
                                        loading={approvalLoading}
                                        onClick={() => handleReject(attendance.id)}
                                    >
                                        반려
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </Modal>

            {/* 연차 일괄등록 모달 */}
            <BulkAnnualLeaveModal
                visible={showBulkLeaveModal}
                onCancel={() => setShowBulkLeaveModal(false)}
                onSuccess={() => {
                    loadUsersAttendance(); // 목록 새로고침
                    setShowBulkLeaveModal(false);
                }}
            />

            {/* 휴가현황 달력 팝업 */}
            <VacationCalendarModal
                visible={vacationCalendarVisible}
                onClose={() => setVacationCalendarVisible(false)}
            />
        </div>
    );
};

export default AttendanceManagementPage;